import React from 'react';
import { cn } from '@/lib/utils';
import { ArrowUpRight } from 'lucide-react';
import { formatTimestamp } from './formatTimestamp';
import { highlightSearchTerm } from './highlightSearch';

export interface MarkdownComponentsOptions {
  /** Files data for resolving citations */
  files?: {
    data: Array<{
      _id: string;
      name?: string;
      teamId?: string;
    }>;
  };
  /** Whether to hide reference links */
  hideReferences?: boolean;
  /** Search term to highlight in content */
  searchTerm?: string;
  /** Callback for timestamp clicks (for in-video navigation) */
  onTimestampClick?: (seconds: number) => void;
}

/**
 * Creates standardized ReactMarkdown components with consistent styling
 * Used by both MultiAskAI and SelectedVideoChat for rendering markdown content
 *
 * @param options - Configuration options for markdown rendering
 * @returns Object containing ReactMarkdown component overrides
 */
export const createMarkdownComponents = (
  options: MarkdownComponentsOptions = {},
) => {
  const {
    files,
    hideReferences = false,
    searchTerm,
    onTimestampClick,
  } = options;

  return {
    h1: ({ children, ...props }: React.ComponentPropsWithoutRef<'h1'>) => (
      <h1
        style={{
          fontSize: '1.125rem',
          fontWeight: 'bold',
          marginBottom: '0.5rem',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </h1>
    ),
    h2: ({ children, ...props }: React.ComponentPropsWithoutRef<'h2'>) => (
      <h2
        style={{
          fontSize: '1rem',
          fontWeight: 'bold',
          marginBottom: '0.5rem',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </h2>
    ),
    h3: ({ children, ...props }: React.ComponentPropsWithoutRef<'h3'>) => (
      <h3
        style={{
          fontSize: '0.9375rem',
          fontWeight: 'bold',
          marginBottom: '0.375rem',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </h3>
    ),
    h4: ({ children, ...props }: React.ComponentPropsWithoutRef<'h4'>) => (
      <h4
        style={{
          fontSize: '0.875rem',
          fontWeight: 'bold',
          marginBottom: '0.375rem',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </h4>
    ),
    h5: ({ children, ...props }: React.ComponentPropsWithoutRef<'h5'>) => (
      <h5
        style={{
          fontSize: '0.8125rem',
          fontWeight: 'bold',
          marginBottom: '0.25rem',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </h5>
    ),
    h6: ({ children, ...props }: React.ComponentPropsWithoutRef<'h6'>) => (
      <h6
        style={{
          fontSize: '0.75rem',
          fontWeight: 'bold',
          marginBottom: '0.25rem',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </h6>
    ),
    p: ({ children, ...props }: React.ComponentPropsWithoutRef<'p'>) => (
      <p
        className="break-words whitespace-pre-wrap mb-3 last:mb-0 text-sm leading-normal [li>&]:mb-0"
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </p>
    ),
    strong: ({
      children,
      ...props
    }: React.ComponentPropsWithoutRef<'strong'>) => (
      <strong
        style={{
          fontWeight: 600,
          display: 'inline',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </strong>
    ),
    ul: ({ children, ...props }: React.ComponentPropsWithoutRef<'ul'>) => (
      <ul className="my-2 pl-6 list-disc" {...props}>
        {children}
      </ul>
    ),
    ol: ({ children, ...props }: React.ComponentPropsWithoutRef<'ol'>) => (
      <ol className="my-2 pl-6 list-decimal" {...props}>
        {children}
      </ol>
    ),
    li: ({ children, ...props }: React.ComponentPropsWithoutRef<'li'>) => (
      <li className="text-sm leading-normal my-1" {...props}>
        {highlightSearchTerm(children as string, searchTerm)}
      </li>
    ),
    table: ({
      children,
      ...props
    }: React.ComponentPropsWithoutRef<'table'>) => (
      <table style={{ borderCollapse: 'collapse', width: '100%' }} {...props}>
        {children}
      </table>
    ),
    th: ({ children, ...props }: React.ComponentPropsWithoutRef<'th'>) => (
      <th
        style={{
          fontSize: '0.875rem',
          border: '1px solid hsl(var(--border))',
          padding: '6px',
          backgroundColor: 'hsl(var(--muted))',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </th>
    ),
    td: ({ children, ...props }: React.ComponentPropsWithoutRef<'td'>) => (
      <td
        style={{
          fontSize: '0.875rem',
          border: '1px solid hsl(var(--border))',
          padding: '6px',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </td>
    ),
    code: ({ children, ...props }: React.ComponentPropsWithoutRef<'code'>) => (
      <code
        style={{
          backgroundColor: 'hsl(var(--muted))',
          padding: '2px 4px',
          borderRadius: '4px',
          fontSize: '0.8125rem',
        }}
        {...props}
      >
        {children}
      </code>
    ),
    blockquote: ({
      children,
      ...props
    }: React.ComponentPropsWithoutRef<'blockquote'>) => (
      <blockquote
        style={{
          fontSize: '0.875rem',
          borderLeft: '3px solid hsl(var(--border))',
          marginLeft: 0,
          paddingLeft: '12px',
          fontStyle: 'italic',
          marginBottom: '0.75rem',
        }}
        {...props}
      >
        {highlightSearchTerm(children as string, searchTerm)}
      </blockquote>
    ),
    hr: ({ ...props }: React.ComponentPropsWithoutRef<'hr'>) => (
      <hr
        style={{
          border: 'none',
          borderTop: '1px solid hsl(var(--border))',
          margin: '1em 0',
        }}
        {...props}
      />
    ),
    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => {
      // Handle file reference format: [→](fileId@timestamp)
      if (href?.includes('@')) {
        if (hideReferences) return null;
        const [fileId, timestamp] = href.split('@');
        const file = files?.data.find((f) => f._id === fileId);

        // If we have a timestamp click handler (single video mode)
        if (onTimestampClick) {
          return (
            <button
              type="button"
              onClick={() => onTimestampClick(Number(timestamp))}
              className={cn(
                'inline cursor-pointer align-middle transition-all duration-150 hover:scale-110 border-0 bg-transparent p-0',
              )}
              title={`Jump to ${formatTimestamp(timestamp)}`}
            >
              <ArrowUpRight
                size={18}
                className={cn(
                  'relative -top-0.5 inline-block px-[2px] align-middle transition-colors duration-150 text-gray-400 hover:text-primary',
                )}
                strokeWidth={1.5}
              />
            </button>
          );
        }

        // Multi-file mode - open in new window
        return (
          <button
            type="button"
            onClick={() => {
              const baseUrl = window.location.pathname.includes('/app')
                ? '/app/#'
                : '/#';
              window.open(
                `${baseUrl}/library/${file?.teamId || 'unknown'}/recents/${fileId}?t=${timestamp}`,
                '_blank',
              );
            }}
            className={cn(
              'inline cursor-pointer align-middle transition-all duration-150 hover:scale-110 border-0 bg-transparent p-0',
            )}
            title={
              file
                ? `Go to: ${file.name} at ${formatTimestamp(timestamp)}`
                : 'Go to source...'
            }
          >
            <ArrowUpRight
              size={18}
              className={cn(
                'relative -top-0.5 inline-block px-[2px] align-middle transition-colors duration-150 text-gray-400 hover:text-primary',
              )}
              strokeWidth={1.5}
            />
          </button>
        );
      }

      // Regular external links
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          {highlightSearchTerm(children as string, searchTerm)}
        </a>
      );
    },
  };
};
